import { useState } from 'react';
import {
  Check,
  X,
  Sparkles,
  Zap,
  Rocket,
  Crown,
  type LucideIcon,
} from 'lucide-react';
import {
  addToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { useAuth } from '../../context/AuthContext';
import { becomeAProvider } from '../../service/userService';
import {
  useFetchAllMembershipPlans,
  useSubmitMembershipMutation,
} from '../../hook/useQueryData';
import { handleLogin } from '../components/auth/apiAuth';
import CustomButton from '../components/CustomButton';
import { cn } from '../../utils/type';
import {
  formatMessagingCell,
  formatPlanFeatureLabel,
  formatPromoToolsCell,
  getAnalyticsValue,
  getFeaturedListingsLimit,
  getMessagingValue,
  getPromoToolsValue,
  isBookingCalendarEnabled,
  isPopularPlan,
} from '../../utils/membershipPlanUtils';
import { Seo } from '../../core/seo';
import FulLoading from '../components/common/ui/loading/FullLoading';
import CheckoutModal, {
  mapApiPlanToCheckout,
  type CheckoutPlan,
} from '../components/common/ui/CheckoutModal';
import BillingCycleToggle from '../components/common/ui/BillingCycleToggle';

type Billing = 'yearly' | 'monthly';
type Cell = string | number | boolean;

const PLAN_TAGLINES: Record<string, string> = {
  'free explorer': 'Start free, no card',
  'growth builder': 'Free for 6 months',
  'business pro': 'For growing teams',
};

const PLAN_ICONS: Record<number, LucideIcon> = {
  0: Zap,
  1: Rocket,
  2: Crown,
};

function getPlanIcon(tier: number): LucideIcon {
  return PLAN_ICONS[tier] ?? Crown;
}

function getPlanTagline(name: string): string {
  return PLAN_TAGLINES[name.toLowerCase()] ?? 'Scale your service business';
}

function isCustomPlan(plan: any): boolean {
  return (
    plan.pricing?.custom || plan.name?.toLowerCase().includes('enterprise')
  );
}

function fmtPrice(plan: any, billing: Billing): string {
  if (plan.tier === 0) return 'Free';
  if (isCustomPlan(plan)) return 'Custom';
  const price =
    billing === 'yearly'
      ? plan.pricing?.yearly?.price
      : plan.pricing?.monthly?.price;
  if (price == null) return 'Custom';
  return `CAD ${Number(price).toFixed(2)}`;
}

function findAddonPrice(plan: any, pattern: RegExp): string | false {
  const addon = plan.addons?.find(
    (a: any) => pattern.test(a.code || '') || pattern.test(a.name || '')
  );
  return addon?.price ?? false;
}

interface ComparisonRow {
  feature: string;
  getValue: (plan: any) => Cell;
}

const COMPARISON_SECTIONS: { title: string; rows: ComparisonRow[] }[] = [
  {
    title: 'Pricing & Limits',
    rows: [
      {
        feature: 'Services included',
        getValue: (p) => p.limits?.services ?? '—',
      },
      {
        feature: 'Team member access',
        getValue: (p) => p.limits?.teamMembers ?? '—',
      },
      {
        feature: 'Featured listings',
        getValue: (p) => getFeaturedListingsLimit(p),
      },
      {
        feature: 'Extra team members',
        getValue: (p) =>
          findAddonPrice(p, /team/i) || (p.tier >= 2 ? '$2 user/month' : false),
      },
      {
        feature: 'Additional services',
        getValue: (p) =>
          findAddonPrice(p, /service/i) ||
          (p.tier >= 2 ? '$2 user/month' : false),
      },
    ],
  },
  {
    title: 'Tools & Features',
    rows: [
      {
        feature: 'Service analytics',
        getValue: (p) => formatPlanFeatureLabel(getAnalyticsValue(p)),
      },
      {
        feature: 'Client booking calendar',
        getValue: (p) => isBookingCalendarEnabled(p),
      },
      {
        feature: 'Discounts & promo tools',
        getValue: (p) => formatPromoToolsCell(getPromoToolsValue(p)),
      },
      {
        feature: 'Messaging system',
        getValue: (p) => formatMessagingCell(getMessagingValue(p)),
      },
      {
        feature: 'Custom booking page branding',
        getValue: (p) => Boolean(p.features?.branding),
      },
    ],
  },
];

function CellValue({ value }: { value: Cell }) {
  if (typeof value === 'boolean') {
    return value ? (
      <span className="h-5 w-5 rounded-full bg-success/15 flex items-center justify-center">
        <Check className="h-3 w-3 text-success" strokeWidth={3} />
      </span>
    ) : (
      <span className="h-5 w-5 rounded-full bg-destructive/10 flex items-center justify-center">
        <X className="h-3 w-3 text-destructive" strokeWidth={3} />
      </span>
    );
  }
  return <span className="text-foreground font-medium">{value}</span>;
}

function PlanHeaderCell({ plan, billing }: { plan: any; billing: Billing }) {
  const Icon = getPlanIcon(plan.tier);
  const popular = isPopularPlan(plan);
  const custom = isCustomPlan(plan);
  const period = billing === 'yearly' ? 'per year' : 'per month';

  return (
    <div
      className={cn(
        'relative px-4 pt-6 pb-3 text-center border-l border-border',
        popular && 'bg-primary/5'
      )}
    >
      {popular && (
        <div className="absolute top-1 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div
            className="flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow"
            style={{ background: 'var(--accent-orange)' }}
          >
            <Sparkles className="h-2.5 w-2.5" /> Most Popular
          </div>
        </div>
      )}
      <div
        className={cn(
          'mx-auto h-8 w-8 rounded-lg flex items-center justify-center mb-1.5',
          popular
            ? 'bg-primary text-primary-foreground'
            : 'bg-primary/10 text-primary'
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-bold text-foreground text-sm">{plan.name}</div>
      <div className="text-[11px] text-muted-foreground mb-1">
        {getPlanTagline(plan.name)}
      </div>
      <div className="text-base font-bold text-foreground leading-tight">
        {fmtPrice(plan, billing)}
      </div>
      {!custom && plan.tier !== 0 && (
        <div className="text-[10px] text-muted-foreground">{period}</div>
      )}
      {plan.freeTrialEnabled && plan.freeTrialDays > 0 ? (
        <div className="mt-1 inline-block text-[10px] font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">
          {plan.freeTrialDays} days free
        </div>
      ) : null}
    </div>
  );
}

function MobilePlanCard({
  plan,
  billing,
  loadingPlanId,
  onSelect,
}: {
  plan: any;
  billing: Billing;
  loadingPlanId: string | null;
  onSelect: (tier: string, annual: boolean) => void;
}) {
  const Icon = getPlanIcon(plan.tier);
  const popular = isPopularPlan(plan);
  const custom = isCustomPlan(plan);
  const period = billing === 'yearly' ? '/year' : '/month';
  const isLoading = loadingPlanId === plan.tier;

  return (
    <div
      className={cn(
        'rounded-2xl border bg-card shadow-sm overflow-hidden',
        popular ? 'border-primary shadow-md' : 'border-border'
      )}
    >
      <div className={cn('p-5', popular && 'bg-primary/5')}>
        {popular && (
          <div
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white mb-2"
            style={{ background: 'var(--accent-orange)' }}
          >
            <Sparkles className="h-2.5 w-2.5" /> Most Popular
          </div>
        )}
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'h-10 w-10 rounded-lg flex items-center justify-center',
              popular
                ? 'bg-primary text-primary-foreground'
                : 'bg-primary/10 text-primary'
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="font-bold text-foreground">{plan.name}</div>
            <div className="text-xs text-muted-foreground">
              {getPlanTagline(plan.name)}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {fmtPrice(plan, billing)}
          </span>
          {!custom && plan.tier !== 0 && (
            <span className="text-xs text-muted-foreground">{period}</span>
          )}
        </div>
        {plan.freeTrialEnabled && plan.freeTrialDays > 0 ? (
          <div className="mt-1 inline-block text-[11px] font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">
            {plan.freeTrialDays} days free trial
          </div>
        ) : null}
      </div>
      <div className="border-t border-border">
        {COMPARISON_SECTIONS.map((section) => (
          <div key={section.title}>
            <div className="px-5 py-2 bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {section.title}
            </div>
            <ul className="divide-y divide-border">
              {section.rows.map((row) => (
                <li
                  key={row.feature}
                  className="px-5 py-2.5 flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-foreground">{row.feature}</span>
                  <span className="shrink-0">
                    <CellValue value={row.getValue(plan)} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-border">
        {custom ? (
          <a
            href="/contact"
            className={cn(
              'w-full flex items-center justify-center font-semibold rounded-lg h-10 text-sm',
              popular
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'border border-primary text-primary hover:bg-primary/5'
            )}
          >
            Contact Sales
          </a>
        ) : (
          <button
            type="button"
            onClick={() => onSelect(plan.tier, billing === 'yearly')}
            disabled={isLoading}
            className={cn(
              'w-full font-semibold rounded-lg h-10 text-sm transition-colors disabled:opacity-60',
              popular
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-foreground text-background hover:bg-foreground/90'
            )}
          >
            {isLoading ? 'Processing...' : `Continue with ${plan.name}`}
          </button>
        )}
      </div>
    </div>
  );
}

const ComparePlans = () => {
  const { isAuthenticated, userId, user } = useAuth();
  const { data, isLoading: plansLoading } = useFetchAllMembershipPlans();
  const { mutate } = useSubmitMembershipMutation();
  const [billing, setBilling] = useState<Billing>('monthly');
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [showFreeConfirmModal, setShowFreeConfirmModal] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<CheckoutPlan | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const plans = [...(data?.plans ?? [])]
    .sort((a, b) => a.tier - b.tier)
    .filter((p) => !p.name?.toLowerCase().includes('enterprise'));

  const gridTemplate = `1.4fr repeat(${plans.length}, 1fr)`;

  const handleBecomeProvider = async () => {
    try {
      if (!userId) throw new Error('User ID is missing');
      if (!isAuthenticated) throw new Error('User is not authenticated');
      const res = await becomeAProvider(userId);
      if (res.success) {
        handleLogin();
        addToast({
          title: 'Success',
          description: 'You are now a service provider.',
          color: 'success',
          radius: 'md',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Error becoming a provider',
        description: err?.message || 'Something went wrong.',
        color: 'danger',
        radius: 'md',
      });
    }
  };

  const handleSelectPlan = async (planId: string, _annual: boolean) => {
    try {
      if (!userId) throw new Error('Please sign in to choose a plan.');
      if (!isAuthenticated) throw new Error('Please sign in to choose a plan.');

      const selected = data?.plans?.find(
        (p: any) => String(p.tier) === String(planId)
      );
      if (!selected) throw new Error('Plan not found');

      if (selected.tier === 0) {
        setShowFreeConfirmModal(true);
        return;
      }

      setCheckoutPlan(mapApiPlanToCheckout(selected));
      setCheckoutOpen(true);
    } catch (err: any) {
      addToast({
        title: 'Subscription Error',
        description: err.message || 'Something went wrong',
        color: 'danger',
        radius: 'md',
      });
    }
  };

  const handleConfirmCheckout = () => {
    if (!checkoutPlan || loadingPlanId !== null) return;
    const isAnnual = billing === 'yearly';
    setLoadingPlanId(checkoutPlan.tier);
    localStorage.setItem('membershipPlanState', 'true');
    localStorage.setItem('membershipPlanName', checkoutPlan.name);
    mutate(
      {
        planId: checkoutPlan.tier,
        isAnnual,
        customerEmail: user?.email,
      },
      { onSettled: () => setLoadingPlanId(null) }
    );
  };

  if (plansLoading) {
    return <FulLoading />;
  }

  return (
    <>
      <Seo
        title="Compare Plans"
        description="Full side-by-side comparison of all HirePanther provider membership plans."
      />

      <div className="-mx-4 md:-mx-6 laptop:-mx-14 min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
        <div className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 pointer-events-none opacity-40">
            <div
              className="absolute -top-32 -left-20 h-72 w-72 rounded-full blur-3xl opacity-50"
              style={{ background: 'var(--gradient-primary)' }}
            />
            <div
              className="absolute -top-20 right-0 h-72 w-72 rounded-full blur-3xl opacity-40"
              style={{ background: 'var(--gradient-popular)' }}
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
            <div className="max-w-3xl">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                Compare Plans
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Find the plan that fits how you run your service business.
              </p>
            </div>

            <div className="mt-3">
              <BillingCycleToggle
                compact
                isAnnual={billing === 'yearly'}
                onChange={(annual) => setBilling(annual ? 'yearly' : 'monthly')}
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="lg:hidden space-y-4">
            {plans.map((plan) => (
              <MobilePlanCard
                key={plan.tier}
                plan={plan}
                billing={billing}
                loadingPlanId={loadingPlanId}
                onSelect={handleSelectPlan}
              />
            ))}
          </div>

          <div className="hidden lg:block">
            <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden pt-2">
              <div
                className="sticky top-0 z-20 grid bg-card/95 backdrop-blur border-b border-border"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <div className="p-3" />
                {plans.map((plan) => (
                  <PlanHeaderCell
                    key={plan.tier}
                    plan={plan}
                    billing={billing}
                  />
                ))}
              </div>

              {COMPARISON_SECTIONS.map((section) => (
                <div key={section.title}>
                  <div
                    className="grid bg-muted/40 border-b border-border"
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    <div className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {section.title}
                    </div>
                    <div style={{ gridColumn: `span ${plans.length}` }} />
                  </div>
                  {section.rows.map((row) => (
                    <div
                      key={row.feature}
                      className="grid border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                      style={{ gridTemplateColumns: gridTemplate }}
                    >
                      <div className="px-4 py-2 text-sm font-medium text-foreground">
                        {row.feature}
                      </div>
                      {plans.map((plan) => (
                        <div
                          key={plan.tier}
                          className={cn(
                            'px-4 py-2 text-sm text-center flex items-center justify-center',
                            isPopularPlan(plan) && 'bg-primary/5'
                          )}
                        >
                          <CellValue value={row.getValue(plan)} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}

              <div
                className="grid border-t border-border bg-muted/20"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <div className="p-2" />
                {plans.map((plan) => {
                  const popular = isPopularPlan(plan);
                  const custom = isCustomPlan(plan);
                  const isLoading = loadingPlanId === plan.tier;
                  return (
                    <div
                      key={plan.tier}
                      className={cn('p-2.5', popular && 'bg-primary/5')}
                    >
                      {custom ? (
                        <a
                          href="/contact"
                          className={cn(
                            'w-full flex items-center justify-center text-xs font-semibold h-9 rounded-lg',
                            popular
                              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                              : 'border border-primary text-primary hover:bg-primary/5'
                          )}
                        >
                          Contact Sales
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            handleSelectPlan(plan.tier, billing === 'yearly')
                          }
                          disabled={isLoading}
                          className={cn(
                            'w-full text-xs font-semibold h-9 rounded-lg transition-colors disabled:opacity-60',
                            popular
                              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                              : 'border border-primary text-primary hover:bg-primary/5'
                          )}
                        >
                          {isLoading
                            ? 'Processing...'
                            : `Continue with ${plan.name}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Questions?{' '}
            <a
              href="/contact"
              className="text-primary font-semibold hover:underline"
            >
              Contact our sales team
            </a>
          </p>
        </div>
      </div>

      <CheckoutModal
        open={checkoutOpen}
        onOpenChange={(open) => {
          if (!open && loadingPlanId !== null) return;
          setCheckoutOpen(open);
          if (!open) setCheckoutPlan(null);
        }}
        plan={checkoutPlan}
        billing={billing}
        isLoading={loadingPlanId !== null}
        onConfirm={handleConfirmCheckout}
      />

      <Modal
        isOpen={showFreeConfirmModal}
        onOpenChange={(open) => {
          if (!open) setShowFreeConfirmModal(false);
        }}
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-foreground">
                  Start Free Explorer Plan?
                </h3>
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-muted-foreground">
                  You are about to activate the Free Explorer plan and become a
                  service provider on this account. Do you want to continue?
                </p>
              </ModalBody>
              <ModalFooter>
                <CustomButton
                  variant="light"
                  color="default"
                  onPress={() => {
                    setShowFreeConfirmModal(false);
                    onClose();
                  }}
                >
                  No
                </CustomButton>
                <CustomButton
                  color="primary"
                  onPress={async () => {
                    await handleBecomeProvider();
                    setShowFreeConfirmModal(false);
                    onClose();
                  }}
                >
                  Yes, continue
                </CustomButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ComparePlans;
